import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, formatError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { TierCards, BillingToggle } from "./NgoProgram";
import MembershipCard from "../components/MembershipCard";
import ImpactReportForm, { ReportStatusList } from "../components/ImpactReportForm";

const pill = { pending: "bg-amber-100 text-amber-800", verified: "bg-green-100 text-green-800", approved: "bg-blue-100 text-blue-800", disbursed: "bg-green-100 text-green-800", rejected: "bg-red-50 text-red-800" };

export default function NgoDashboard() {
  const { user, loading } = useAuth();
  const [me, setMe] = useState(null);
  const [projects, setProjects] = useState([]);
  const [billing, setBilling] = useState("yearly");
  const [busy, setBusy] = useState(false);
  const [reports, setReports] = useState([]);
  const load = () => {
    api.get("/ngos/me").then((r) => setMe(r.data)).catch(() => {});
    api.get("/ngos/projects/mine").then((r) => setProjects(r.data.items)).catch(() => {});
    api.get("/ngos/reports/mine").then((r) => setReports(r.data.items)).catch(() => {});
  };
  useEffect(() => { if (user?.role === "ngo") load(); }, [user]);
  useEffect(() => { if (me?.ngo?.subscription?.billing) setBilling(me.ngo.subscription.billing); }, [me?.ngo?.subscription?.billing]);

  if (loading) return <div className="p-10">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "ngo") return <Navigate to="/ngo" replace />;
  if (!me) return <div className="p-10">Loading…</div>;
  const { ngo, subscription_active: active, tier, requests_used } = me;

  const subscribe = async (t) => {
    setBusy(true);
    try { const { data } = await api.post("/ngos/subscribe", { tier_key: t.key, billing, origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(formatError(e)); setBusy(false); }
  };

  return (
    <div data-testid="ngo-dashboard" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="overline">NGO Dashboard</div>
      <div className="flex flex-wrap items-center gap-3 mt-2">
        <h1 className="font-serif text-4xl sm:text-5xl text-[#4A0E17]">{ngo.org_name}</h1>
        <span data-testid="ngo-status" className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${pill[ngo.status]}`}>{ngo.status}</span>
      </div>
      {ngo.status === "pending" && <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-900">Your documents are being checked. Once you're verified you can choose a tier and request funding.</div>}
      {ngo.status === "rejected" && <div className="mt-8 bg-red-50 border border-red-200 rounded-xl p-5 text-sm text-red-900">Verification was not approved{ngo.review_note ? `: ${ngo.review_note}` : ""}. Please contact seva@panchamdham.org.</div>}

      {ngo.status === "verified" && (
        <>
          <div data-testid="ngo-subscription" className="mt-8 bg-[#FFFDF9] sacred-border rounded-2xl p-6 flex flex-wrap items-center gap-6">
            <div className="flex-1">
              <div className="overline">Subscription</div>
              {active ? <div className="font-serif text-2xl text-[#4A0E17] mt-1">{tier.name} · active until {ngo.subscription.active_until}</div>
                : <div className="font-serif text-2xl text-[#4A0E17] mt-1">No active subscription</div>}
              {active && <div className="text-sm text-[#63534E] mt-1">Requests used this year: {requests_used}{tier.max_requests_per_year ? ` / ${tier.max_requests_per_year}` : " (unlimited)"} · up to ${tier.max_amount_usd.toLocaleString()} per project</div>}
            </div>
            <BillingToggle billing={billing} setBilling={setBilling} />
          </div>
          <div className="mt-6"><MembershipCard kind="ngo" /></div>
          <div className="mt-6"><TierCards billing={billing} onPick={subscribe} currentKey={active ? tier.key : null} busy={busy} /></div>
          {active && <ProjectForm max={tier.max_amount_usd} onSaved={load} />}
        </>
      )}

      <section className="mt-14">
        <h2 className="font-serif text-2xl text-[#4A0E17]">Your project requests</h2>
        <div className="mt-5 space-y-3">
          {projects.length === 0 && <div className="text-sm text-[#63534E]">No requests yet.</div>}
          {projects.map((p) => (
            <div key={p.id} data-testid={`ngo-project-${p.id}`} className="bg-[#FFFDF9] sacred-border rounded-xl p-5 flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-0"><div className="font-semibold text-[#4A0E17]">{p.title}</div><div className="text-xs text-[#63534E]">{p.beneficiaries} beneficiaries · requested ${p.amount_requested_usd.toLocaleString()}</div></div>
              {p.amount_granted_usd > 0 && <div className="font-mono text-sm text-green-800">granted ${p.amount_granted_usd.toLocaleString()}</div>}
              <span className={`px-2 py-0.5 rounded-full text-xs capitalize ${pill[p.status]}`}>{p.status}</span>
              <div className="basis-full">
                <ImpactReportForm project={p} onSaved={load} />
                <ReportStatusList reports={reports.filter((r) => r.project_id === p.id)} />
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function ProjectForm({ max, onSaved }) {
  const blank = { title: "", description: "", category: "community", amount_requested_usd: "", beneficiaries: "", timeline: "" };
  const [f, setF] = useState(blank);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/ngos/projects", { ...f, amount_requested_usd: parseFloat(f.amount_requested_usd), beneficiaries: parseInt(f.beneficiaries) });
      toast.success("Project request submitted"); setF(blank); onSaved();
    } catch (err) { toast.error(formatError(err)); }
  };
  return (
    <form onSubmit={submit} data-testid="ngo-project-form" className="mt-10 bg-[#FFFDF9] sacred-border rounded-2xl p-6 grid sm:grid-cols-2 gap-3">
      <h2 className="sm:col-span-2 font-serif text-2xl text-[#4A0E17]">Request project funding</h2>
      <Input data-testid="np-title" required minLength={5} placeholder="Project title" value={f.title} onChange={set("title")} className="bg-white h-11 sm:col-span-2" />
      <Textarea data-testid="np-description" required minLength={30} rows={4} placeholder="What will you do, for whom, and how will you report back? (at least 30 characters)" value={f.description} onChange={set("description")} className="bg-white sm:col-span-2" />
      <select data-testid="np-category" value={f.category} onChange={set("category")} className="h-11 rounded-md border border-[#D4AF37]/40 bg-white px-3 text-sm">
        {["community", "education", "health", "gau-seva", "temple", "environment", "disaster-relief"].map((c) => <option key={c}>{c}</option>)}
      </select>
      <Input data-testid="np-amount" required type="number" min="1" max={max} placeholder={`Amount needed (USD, max ${max})`} value={f.amount_requested_usd} onChange={set("amount_requested_usd")} className="bg-white h-11" />
      <Input data-testid="np-beneficiaries" required type="number" min="1" placeholder="Number of beneficiaries" value={f.beneficiaries} onChange={set("beneficiaries")} className="bg-white h-11" />
      <Input data-testid="np-timeline" placeholder="Timeline (e.g. Dec 2026 – Feb 2027)" value={f.timeline} onChange={set("timeline")} className="bg-white h-11" />
      <Button data-testid="np-submit" className="sm:col-span-2 bg-[#4A0E17] hover:bg-[#36080F] rounded-full h-11">Submit request</Button>
    </form>
  );
}
